import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { statsAPI, feedAPI, eventsAPI, jobsAPI, usersAPI } from '../lib/api';
import {
  Briefcase, Home, Heart, Users, DollarSign, Scale,
  Stethoscope, Plane, ArrowRight, TrendingUp, UserCheck, Star,
  Calendar, MessageCircle, Bell, MapPin, Clock, Search, X, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import usePageTitle from '../hooks/usePageTitle';

const SERVICE_CATEGORIES = [
  { id: 'career', name: 'Career Services', icon: Briefcase, description: 'Jobs, coaching & resume reviews', gradient: 'from-blue-500 to-blue-600', bg: 'bg-gradient-to-br from-blue-50 to-blue-100', text: 'text-blue-900', stats: '1,200+ Jobs' },
  { id: 'real_estate', name: 'Real Estate', icon: Home, description: 'Find trusted alumni realtors', gradient: 'from-emerald-500 to-emerald-600', bg: 'bg-gradient-to-br from-emerald-50 to-emerald-100', text: 'text-emerald-900', stats: '150+ Realtors' },
  { id: 'social_dating', name: 'Social & Dating', icon: Heart, description: 'Connect with fellow alumni', gradient: 'from-rose-500 to-rose-600', bg: 'bg-gradient-to-br from-rose-50 to-rose-100', text: 'text-rose-900', stats: '50+ Events/Month' },
  { id: 'networking', name: 'Networking', icon: Users, description: 'Expand your professional circle', gradient: 'from-fuchsia-500 to-fuchsia-600', bg: 'bg-gradient-to-br from-fuchsia-50 to-fuchsia-100', text: 'text-fuchsia-900', stats: '5,000+ Profiles' },
  { id: 'financial', name: 'Financial Services', icon: DollarSign, description: 'Financial advisors & planning', gradient: 'from-amber-500 to-amber-600', bg: 'bg-gradient-to-br from-amber-50 to-amber-100', text: 'text-amber-900', stats: '80+ Advisors' },
  { id: 'legal', name: 'Legal Services', icon: Scale, description: 'Attorney consultations', gradient: 'from-red-500 to-red-600', bg: 'bg-gradient-to-br from-red-50 to-red-100', text: 'text-red-900', stats: '120+ Attorneys' },
  { id: 'healthcare', name: 'Healthcare', icon: Stethoscope, description: 'Medical professionals network', gradient: 'from-teal-500 to-teal-600', bg: 'bg-gradient-to-br from-teal-50 to-teal-100', text: 'text-teal-900', stats: '200+ Providers' },
  { id: 'travel', name: 'Travel', icon: Plane, description: 'Travel agents & group trips', gradient: 'from-indigo-500 to-indigo-600', bg: 'bg-gradient-to-br from-indigo-50 to-indigo-100', text: 'text-indigo-900', stats: '45+ Agents' },
];

const Dashboard = () => {
  usePageTitle('Dashboard');
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Member search
  const [memberQuery, setMemberQuery] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberSearchOpen, setMemberSearchOpen] = useState(false);
  const memberDebounceRef = useRef(null);

  const handleMemberSearch = (value) => {
    setMemberQuery(value);
    if (memberDebounceRef.current) clearTimeout(memberDebounceRef.current);
    if (!value.trim()) { setMemberResults([]); return; }
    memberDebounceRef.current = setTimeout(async () => {
      setMemberLoading(true);
      try {
        const params = { search: value.trim(), limit: 8 };
        if (user?.institution) params.institution = user.institution;
        const res = await usersAPI.listUsers(params);
        setMemberResults(Array.isArray(res.data) ? res.data.filter(u => u.user_id !== user?.user_id) : []);
      } catch { setMemberResults([]); }
      finally { setMemberLoading(false); }
    }, 300);
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsRes, postsRes, eventsRes, jobsRes] = await Promise.allSettled([
          statsAPI.get(),
          feedAPI.list({ limit: 3 }),
          eventsAPI.list({ limit: 3 }),
          jobsAPI.list({ limit: 4 }),
        ]);
        if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
        if (postsRes.status === 'fulfilled') setRecentPosts(postsRes.value.data?.slice?.(0, 3) || []);
        if (eventsRes.status === 'fulfilled') setUpcomingEvents(eventsRes.value.data?.slice?.(0, 3) || []);
        if (jobsRes.status === 'fulfilled') setRecentJobs(jobsRes.value.data?.slice?.(0, 4) || []);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl text-dark-slate mb-2">
            Welcome Back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}! <span className="inline-block animate-bounce">👋</span>
          </h1>
          <p className="text-slate-gray text-lg">
            {user?.institution
              ? `GradLink for ${user.institution} — your alumni network, reimagined`
              : 'Explore services and connect with your alumni community'}
          </p>
          {user?.institution && (
            <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-emerald-800 font-semibold text-sm">{user.institution}</span>
            </div>
          )}
        </div>

        {/* Onboarding / Profile Completion */}
        {(user?.profile_completion || 0) < 80 && (
          <div className="bg-gradient-to-r from-dark-slate to-slate-700 rounded-3xl p-6 mb-8 text-white shadow-lg">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-heading font-bold text-xl mb-1">Complete Your Profile</h3>
                <p className="text-white/70 text-sm">
                  To receive AI-powered mentor matches, dating matches, and unlock the full service directory, complete your profile.
                </p>
              </div>
              <span className="bg-white/20 rounded-full px-3 py-1 text-sm font-bold">{user?.profile_completion || 0}%</span>
            </div>
            <Progress value={user?.profile_completion || 0} className="h-2 mb-4 bg-white/20" />
            <div className="flex items-center space-x-3">
              <Link to="/profile">
                <Button className="bg-white text-dark-slate hover:bg-white/90 rounded-full font-semibold">
                  Complete Profile <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              {!user?.institution && (
                <span className="text-white/60 text-xs">Start by selecting your institution</span>
              )}
            </div>
          </div>
        )}

        {/* Service Categories Grid */}
        <div className="mb-10">
          <h2 className="font-heading font-bold text-2xl text-dark-slate mb-6">Explore Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICE_CATEGORIES.map((category, index) => (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                data-testid={`category-${category.id}`}
                className={`${category.bg} rounded-3xl p-6 border border-slate-100 shadow-ambient hover:shadow-hover hover:-translate-y-0.5 transition-all`}
              >
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${category.gradient} flex items-center justify-center mb-4 shadow-lg`}>
                  <category.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className={`font-heading font-bold text-lg ${category.text} mb-1`}>{category.name}</h3>
                <p className="text-slate-gray text-sm mb-3">{category.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-gray">{category.stats}</span>
                  <ArrowRight className={`w-4 h-4 ${category.text}`} />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Find Members */}
        <div className="mb-10">
          <h2 className="font-heading font-bold text-2xl text-dark-slate mb-4">Find Alumni</h2>
          <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-ambient">
            <div className="flex items-center gap-3">
              <Search className="w-5 h-5 text-slate-gray flex-shrink-0" />
              <Input
                placeholder="Search members by name, title, or company..."
                value={memberQuery}
                onChange={(e) => { setMemberSearchOpen(true); handleMemberSearch(e.target.value); }}
                onFocus={() => { if (memberQuery.trim()) setMemberSearchOpen(true); }}
                className="h-10 rounded-xl bg-slate-50 border-slate-200"
              />
              {memberQuery && (
                <button onClick={() => { setMemberQuery(''); setMemberResults([]); setMemberSearchOpen(false); }}
                  className="p-1.5 rounded-full hover:bg-slate-100">
                  <X className="w-4 h-4 text-slate-gray" />
                </button>
              )}
            </div>
            {memberSearchOpen && memberLoading && (
              <div className="flex items-center justify-center py-4 mt-3">
                <Loader2 className="w-5 h-5 animate-spin text-slate-gray" />
              </div>
            )}
            {memberSearchOpen && !memberLoading && memberResults.length > 0 && (
              <div className="mt-3 space-y-2 max-h-72 overflow-y-auto">
                {memberResults.map(m => (
                  <Link key={m.user_id} to={`/profile/${m.user_id}`}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                    <Avatar className="w-10 h-10 flex-shrink-0">
                      <AvatarImage src={m.profile_photo} />
                      <AvatarFallback className="bg-mint-fresh text-dark-slate text-sm">{m.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-dark-slate truncate">{m.name}</p>
                      <p className="text-xs text-slate-gray truncate">
                        {[m.title, m.company].filter(Boolean).join(' at ') || m.institution || 'Alumni'}
                      </p>
                    </div>
                    {m.institution && (
                      <Badge variant="outline" className="text-[10px] flex-shrink-0 hidden sm:inline-flex">{m.institution}</Badge>
                    )}
                  </Link>
                ))}
              </div>
            )}
            {memberSearchOpen && !memberLoading && memberQuery.trim() && memberResults.length === 0 && (
              <p className="text-sm text-slate-gray text-center py-3 mt-2">No members found</p>
            )}
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
            <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            <p className="font-heading font-bold text-3xl text-dark-slate mb-1">
              {stats?.total_alumni?.toLocaleString() || '—'}
            </p>
            <p className="text-slate-gray">Active Alumni</p>
          </div>
          <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
            <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4">
              <UserCheck className="w-6 h-6 text-emerald-600" />
            </div>
            <p className="font-heading font-bold text-3xl text-dark-slate mb-1">
              {stats?.service_providers?.toLocaleString() || '—'}
            </p>
            <p className="text-slate-gray">Service Providers</p>
          </div>
          <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center mb-4">
              <Star className="w-6 h-6 text-amber-600" />
            </div>
            <p className="font-heading font-bold text-3xl text-dark-slate mb-1">
              {stats?.satisfaction_rate || '—'}%
            </p>
            <p className="text-slate-gray">Satisfaction Rate</p>
          </div>
        </div>

        {/* Two-column layout: Activity + Events/Jobs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          {/* Recent Activity */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-xl text-dark-slate">Recent Activity</h2>
              <Link to="/feed" className="text-sm text-slate-gray hover:text-dark-slate flex items-center">
                View All <ArrowRight className="w-3 h-3 ml-1" />
              </Link>
            </div>
            {recentPosts.length === 0 ? (
              <div className="bg-white rounded-3xl p-8 text-center border border-slate-100 shadow-ambient">
                <MessageCircle className="w-10 h-10 text-slate-gray mx-auto mb-3" />
                <p className="text-slate-gray">No posts yet. Be the first to share!</p>
                <Link to="/feed" className="inline-flex items-center mt-3 text-dark-slate font-semibold hover:underline text-sm">
                  Go to Feed <ArrowRight className="w-3 h-3 ml-1" />
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentPosts.map(post => (
                  <Link key={post.post_id} to="/feed"
                    className="block bg-white rounded-2xl p-5 border border-slate-100 shadow-ambient hover:shadow-hover transition-shadow">
                    <div className="flex items-start space-x-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={post.user_photo} />
                        <AvatarFallback className="bg-mint-fresh text-dark-slate text-sm">
                          {post.user_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-dark-slate text-sm">{post.user_name}</p>
                        <p className="text-slate-gray text-sm line-clamp-2 mt-0.5">{post.content}</p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-slate-gray">
                          <span className="flex items-center"><Heart className="w-3 h-3 mr-1" /> {post.likes || 0}</span>
                          <span className="flex items-center"><MessageCircle className="w-3 h-3 mr-1" /> {post.comments?.length || 0}</span>
                          <span>{post.created_at ? formatDistanceToNow(new Date(post.created_at), { addSuffix: true }) : ''}</span>
                        </div>
                      </div>
                      {post.image_url && (
                        <img src={post.image_url} alt="" className="w-16 h-16 rounded-xl object-cover" />
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar: Upcoming Events + Jobs */}
          <div className="space-y-6">
            {/* Upcoming Events */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-bold text-xl text-dark-slate">Upcoming Events</h2>
                <Link to="/events" className="text-sm text-slate-gray hover:text-dark-slate flex items-center">
                  All <ArrowRight className="w-3 h-3 ml-1" />
                </Link>
              </div>
              {upcomingEvents.length === 0 ? (
                <div className="bg-white rounded-2xl p-5 text-center border border-slate-100 shadow-ambient">
                  <Calendar className="w-8 h-8 text-slate-gray mx-auto mb-2" />
                  <p className="text-slate-gray text-sm">No upcoming events</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingEvents.map(event => (
                    <Link key={event.event_id} to="/events"
                      className="block bg-white rounded-2xl p-4 border border-slate-100 shadow-ambient hover:shadow-hover transition-shadow">
                      <p className="font-semibold text-dark-slate text-sm line-clamp-1">{event.title}</p>
                      <div className="flex items-center text-xs text-slate-gray mt-1.5 space-x-3">
                        <span className="flex items-center"><Clock className="w-3 h-3 mr-1" /> {event.date}</span>
                        {event.location && <span className="flex items-center"><MapPin className="w-3 h-3 mr-1" /> {event.location}</span>}
                      </div>
                      {event.rsvp_count > 0 && (
                        <p className="text-xs text-emerald-600 mt-1">{event.rsvp_count} attending</p>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Jobs */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-bold text-xl text-dark-slate">Latest Jobs</h2>
                <Link to="/jobs" className="text-sm text-slate-gray hover:text-dark-slate flex items-center">
                  All <ArrowRight className="w-3 h-3 ml-1" />
                </Link>
              </div>
              {recentJobs.length === 0 ? (
                <div className="bg-white rounded-2xl p-5 text-center border border-slate-100 shadow-ambient">
                  <Briefcase className="w-8 h-8 text-slate-gray mx-auto mb-2" />
                  <p className="text-slate-gray text-sm">No jobs posted yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentJobs.map(job => (
                    <Link key={job.job_id} to="/jobs"
                      className="block bg-white rounded-2xl p-4 border border-slate-100 shadow-ambient hover:shadow-hover transition-shadow">
                      <p className="font-semibold text-dark-slate text-sm line-clamp-1">{job.title}</p>
                      <p className="text-xs text-slate-gray mt-0.5">{job.company}</p>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-xs text-slate-gray flex items-center">
                          <MapPin className="w-3 h-3 mr-1" /> {job.location || 'Remote'}
                        </span>
                        {(job.salary_min || job.salary_max) && (
                          <Badge variant="secondary" className="text-xs bg-emerald-50 text-emerald-700">
                            ${(job.salary_min || 0).toLocaleString()}+
                          </Badge>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient">
          <h2 className="font-heading font-bold text-xl text-dark-slate mb-4">Quick Links</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {[
              { to: '/jobs', icon: Briefcase, label: 'Browse Jobs' },
              { to: '/events', icon: Calendar, label: 'Events' },
              { to: '/mentorship', icon: UserCheck, label: 'Find Mentor' },
              { to: '/services', icon: Star, label: 'Services' },
              { to: '/bookings', icon: Calendar, label: 'Bookings' },
              { to: '/chat', icon: MessageCircle, label: 'Messages' },
            ].map(link => (
              <Link key={link.to} to={link.to}
                className="flex items-center space-x-2 p-3 rounded-xl bg-slate-50 hover:bg-mint-fresh/30 transition-colors"
                data-testid={`quick-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}>
                <link.icon className="w-5 h-5 text-dark-slate" />
                <span className="font-medium text-dark-slate text-sm">{link.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
